import awsConfig from '../../auth-config';
import AWS from 'aws-sdk';
import {API, Auth} from 'aws-amplify';
import ApiResponse from './ApiResponse';

const API_NAME = 'GreenleafApi'; //from auth-config.js

function buildQueryString(query) {
  if (!query || Object.keys(query).length === 0) return '';
  let string = ['?'];

  Object.keys(query).forEach(key => {
    string.push(`${key}=${query[key]}&`);
  });

  return string.join('').slice(0, -1);
}

const getRequestOptions = (method, body) => {
  return {
    method: method,
    body,
    headers: {'Content-Type': 'application/json'}
  };
};

const callApi = async (url, requestOptions = {method: 'get'}) => {
  try {
    try {
      let session = await Auth.currentSession();
      requestOptions.headers = requestOptions.headers || {};
      requestOptions.headers['Authorization'] = session.idToken.jwtToken;
    } catch (e) {
      if (e !== 'No current user') {
        console.error(e);
      }
    }
    const requestMethod = requestOptions.method;
    delete requestOptions.method;

    let res;
    switch (requestMethod) {
      case 'delete':
        res = await API.del(API_NAME, url, requestOptions);
        break;
      case 'upload':
        delete requestOptions.headers['Content-Type'];
        res = await fetch(`${awsConfig.API.endpoints[0].endpoint}${url}`, {
          method: 'post',
          body: requestOptions.body,
          headers: requestOptions.headers
        });
        const json = await res.json();
        if (res.status === 500) {
          throw new ApiResponse(null, null, json.errors);
        }
        return new ApiResponse(json, res.headers, null);
      default:  //get, post, put
        requestOptions.response = true;
        res = await API[requestMethod](API_NAME, url, requestOptions);
    }

    return new ApiResponse(res.data || res, res.headers);
  } catch (e) {
    console.warn(e, e.response); // e.response
    if (e.response && e.response.status > 399 && e.response.status < 500) {
      throw new ApiResponse(null, null, e.response.data.errors);
    } else {
      throw new ApiResponse(null, null, [{
        code: '0',
        msg: 'Internal server error. Please try again later.',
        src: 'Unknown'
      }]);
    }
  }
};

const GreenLeafApi = {

  Company: {
    get: (query) => {
      const url = ['/company/find', buildQueryString(query)];

      return callApi(url.join(''));
    },
    update: (company) => {
      return callApi(`/company/${company.id}`, getRequestOptions('put', company));
    },
    create: (company) => {
      let c = company;
      c.date_added = new Date(Date.now());
      return callApi('/company/new', getRequestOptions('post', c));
    },
    delete: (companyId) => {
      return callApi(`/company/${companyId}`, getRequestOptions('delete'));
    }
  },

  Program: {
    get: (query) => {
      const url = ['/program/find', buildQueryString(query)];

      return callApi(url.join(''));
    },
    getOverallScore: (query) => {
      const url = ['/program/', buildQueryString(query)];

      return callApi(url.join(''));
    },
    update: (program) => {
      return callApi(`/program/${program.id}`, getRequestOptions('put', program));
    },
    create: (program) => {
      return callApi('/program/new', getRequestOptions('post', program));
    },
    delete: (programId) => {
      return callApi(`/program/${programId}`, getRequestOptions('delete'));
    }
  },

  Team: {
    get: (query) => {
      const url = ['/team/find', buildQueryString(query)];

      return callApi(url.join(''));
    },
    update: (team) => {
      return callApi(`/team/${team.id}`, getRequestOptions('put', team));
    },
    create: (team) => {
      return callApi('/team/new', getRequestOptions('post', team));
    },
    delete: (teamId) => {
      return callApi(`/team/${teamId}`, getRequestOptions('delete'));
    }
  },

  Contact: {
    get: (query) => {
      const url = ['/contact/find', buildQueryString(query)];

      return callApi(url.join(''));
    },
    getInfo: (query) => {
      const url = ['/contact/info', buildQueryString(query)];

      return callApi(url.join(''));
    },
    update: (contact) => {
      return callApi(`/contact/${contact.id}`, getRequestOptions('put', contact));
    },
    create: (contact) => {
      return callApi('/contact/new', getRequestOptions('post', contact));
    },
    delete: (contactId) => {
      return callApi(`/contact/${contactId}`, getRequestOptions('delete'));
    }
  },

  TeamContact: {
    get: (query) => {
      const url = ['/team_contact/find', buildQueryString(query)];

      return callApi(url.join(''));
    },
    update: (team_contact) => {
      return callApi(`/team_contact/${team_contact.contact_id}/${team_contact.team_id}/${team_contact.type_id}`,
          getRequestOptions('put', team_contact));
    },
    create: (team_contact) => {
      return callApi('/team_contact/new', getRequestOptions('post', team_contact));
    },
    delete: (team_contact) => {
      return callApi(`/team_contact/${team_contact.contact_id}/${team_contact.team_id}/${team_contact.type_id}`,
          getRequestOptions('delete'));
    }
  },

  Assessment: {
    get: async (id) => {
      const user = await Auth.currentUserInfo();
      return callApi(`${!user ? '/public' : '' }/assessment/${id}`);
    },
    getSubs: async (query) => {
      const user = await Auth.currentUserInfo();
      const url = [`${!user ? '/public' : '' }/assessment/`, buildQueryString(query)];

      return callApi(url.join(''));
    },
    getSubsToSelect: async (query) => {
      const user = await Auth.currentUserInfo();
      const url = [`${!user ? '/public' : '' }/assessment/find`, buildQueryString(query)];

      return callApi(url.join(''));
    },
    create: (assessment) => {
      return callApi('/assessment', getRequestOptions('post', {assessment}));
    },
    find: (query) => {
      const url = ['/assessment/find', buildQueryString(query)];
      return callApi(url.join(''));
    }
  },

  TeamAssessment: {
    get: async (query) => {
      const url = ['/team_assessment/find', buildQueryString(query)];
      return await callApi(url.join(''));
    },
    getScore: (query) => {
      const url = ['/team_assessment/', buildQueryString(query)];

      return callApi(url.join(''));
    },
    getSubScores: (query) => {
      const url = ['/team_assessment/subscores/', buildQueryString(query)];

      return callApi(url.join(''));
    },
    getAnswers: (query) => {
      const url = ['/assessment_answers/', buildQueryString(query)];

      return callApi(url.join(''));
    },
    create: async (teamAssessment) => {
      let response = await callApi('/team_assessment/',
        getRequestOptions('post', teamAssessment));
      teamAssessment.id = parseInt(response.headers.location.split('/')[1], 10);
      return teamAssessment;
    },
    sendAnswer: async (answer, teamAssessmentId) => {
      return callApi(`/assessment_answers/${teamAssessmentId}`,
        getRequestOptions('put', answer));
    },
    getLatestAssessmentAnswers: async (team_id) => {
      return await callApi(`/assessment_answers/current/${team_id}`);
    },
    getOverallScore: async (query) => {
      const url = ['/team_assessment/overall', buildQueryString(query)];

      return callApi(url.join(''));
    },
    complete: async (teamAssessmentId) => {
      return await callApi(`/team_assessment/${teamAssessmentId}`,
        getRequestOptions('put'));
    },
    createRandom: (rando) => {
      return callApi(`/team_assessment/${rando.teamId}`, getRequestOptions('post', {date: rando.date, assessment_id: 1}));
    }
  },

  GroupTeamAssessment: {
    getScore: (query) => {
      const url = ['/group_team_assessment/', buildQueryString(query)];

      return callApi(url.join(''));
    },

    getSubScores: (query) => {
      const url = ['/group_team_assessment/subscores/', buildQueryString(query)];

      return callApi(url.join(''));
    },

    create: (team_id, assessment_id, emails, subsection_ids) => {
      let body = { team_id, assessment_id, emails, src: window.location.origin, subsection_ids };
      return callApi('/group_team_assessment/new',
        getRequestOptions('post', body));
    },

    get: (query) => {
      const url = ['/group_team_assessment/find', buildQueryString(query)];

      return callApi(url.join(''));
    },

    getInvite: (token) => {
      return callApi(`/public/group_team_assessment/find/${token}`);
    },

    submit: (answers, token) => {
      return callApi('/public/group_team_assessment_answers/', getRequestOptions('post', { token, answers }));
    },
    createRandom: (rando) => {
      return callApi(`/group_team_assessment/${rando.teamId}`, getRequestOptions('post', {date: rando.date, assessment_id: 1, assessment_count: rando.assessment_count}));
    },
    getAnswerAverages: (groupAssessmentId) => {
      return callApi(`/group_team_assessment_answers/${groupAssessmentId}`);
    },
    closeAssessment: (groupAssessmentId) => {
      return callApi(`/group_team_assessment/${groupAssessmentId}`, getRequestOptions('put'));
    },
    resendInvite: (invite) => {
      return callApi('/group_team_assessment/resend', getRequestOptions('post', { invite, src: window.location.origin }));
    }
  },

  CompanyUser: {
    get: (query) => {
      const url = ['/company_user/find', buildQueryString(query)];

      return callApi(url.join(''));
    },
    create: (company_user) => {
      return callApi('/company_user/new', getRequestOptions('post', company_user));
    },
    delete: (company_user) => {
      return callApi(`/company_user/remove?email=${company_user.email}&companyId=${company_user.company_id}`,
          getRequestOptions('delete'));
    }
  },

  User: {
    get: async (username) => {
      try {
        let params = {
          UserPoolId: awsConfig.Auth.userPoolId,
          Username: username
        };
        const provider = new AWS.CognitoIdentityServiceProvider();
        return new Promise((resolve, reject) => {
          provider.adminGetUser(params, (error, data) => {
            if (error) {
              reject();
            }
            resolve(data);
          });
        });
      } catch (e) {
        console.error(e);
      }
    },
    list: () => {
      let params = {
        UserPoolId: awsConfig.Auth.userPoolId,
        AttributesToGet: null,
        Limit: 0,
      };
      const provider = new AWS.CognitoIdentityServiceProvider();
      return new Promise((resolve, reject) => {
        provider.listUsers(params, (error, data) => {
          if (error) {
            reject();
          }
          resolve(data.Users);
        });
      });
    },
    update: (user) => {
      const params = {
        Username: user.email,
        UserPoolId: awsConfig.Auth.userPoolId,
        UserAttributes: [
          { Name: 'custom:role', Value: user.role },
          { Name: 'custom:first_name', Value: user.first_name },
          { Name: 'custom:last_name', Value: user.last_name }
        ]
      };

      const provider = new AWS.CognitoIdentityServiceProvider();
      return new Promise((resolve, reject) => {
        provider.adminUpdateUserAttributes(params, (error) => {
          if (error) {
            reject();
          }
          resolve();
        });
      });
    },
    delete: (username) => {
      const params = {
        UserPoolId: awsConfig.Auth.userPoolId,
        Username: username
      };
      const provider = new AWS.CognitoIdentityServiceProvider();

      return new Promise((resolve, reject) => {
        provider.adminDeleteUser(params, (err, data) => {
          if (err) reject(err);
          resolve(data);
        });
      });
    },
    inviteUser: (params) => {
      params.UserPoolId = awsConfig.Auth.userPoolId;
      const provider = new AWS.CognitoIdentityServiceProvider();
      return new Promise((resolve, reject) => {
        provider.adminCreateUser(params, (err, data) => {
          if (err) reject(err);
          resolve(data);
        });
      });
    }
  },

  Engagement: {
    get: (query) => {
      const url = ['/engagement', buildQueryString(query)];

      return callApi(url.join(''));
    },
    create: (engagement) => {
      return callApi('/engagement', getRequestOptions('post', engagement));
    },
    update: (engagement) => {
      return callApi('/engagement', getRequestOptions('put', engagement));
    },
    delete: (engagementId) => {
      return callApi(`/engagement/${engagementId}`, getRequestOptions('delete'));
    },
    Goal: {
      get: (engagementId, query) => {
        const url = [`/engagement/${engagementId}/goals`, buildQueryString(query)];

        return callApi(url.join(''));
      },
      create: (engagementId, goal) => {
        return callApi(`/engagement/${engagementId}/goals`, getRequestOptions('post', goal));
      },
      update: (engagementId, goal) => {
        return callApi(`/engagement/${engagementId}/goals`, getRequestOptions('put', goal));
      },
      delete: (engagementId, goalId) => {
        return callApi(`/engagement/${engagementId}/goals/${goalId}`, getRequestOptions('delete'));
      },
    },
    Contract: {
      get: (engagementId, query) => {
        const url = [`/engagement/${engagementId}/contracts`, buildQueryString(query)];

        return callApi(url.join(''));
      },
      create: (engagementId, contract) => {
        return callApi(`/engagement/${engagementId}/contracts`, getRequestOptions('post', contract));
      },
      update: (engagementId, contract) => {
        return callApi(`/engagement/${engagementId}/contracts`, getRequestOptions('put', contract));
      },
      delete: (engagementId, contractId) => {
        return callApi(`/engagement/${engagementId}/contracts/${contractId}`, getRequestOptions('delete'));
      },
    },
    Agreement: {
      create: (engagementId, agreement) => {
        return callApi(`/engagement/${engagementId}/agreements`, getRequestOptions('post', agreement));
      },
    },
    Note: {
      get: (engagementId, query) => {
        const url = [`/engagement/${engagementId}/notes`, buildQueryString(query)];

        return callApi(url.join(''));
      },
      create: (engagementId, note) => {
        return callApi(`/engagement/${engagementId}/notes`, getRequestOptions('post', note));
      },
      update: (engagementId, note) => {
        return callApi(`/engagement/${engagementId}/notes`, getRequestOptions('put', note));
      },
      delete: (engagementId, noteId) => {
        return callApi(`/engagement/${engagementId}/notes/${noteId}`, getRequestOptions('delete'));
      }
    },
    Attachment: {
      upload: (engagementId, data) => {
        return callApi(`/engagement/upload/${engagementId}`, getRequestOptions('upload', data));
      },
      get: (engagementId) => {
        return callApi(`/engagement/upload/${engagementId}`);
      },
      delete: (engagementId, file_location) => {
        return callApi(`/engagement/upload/${engagementId}?file_location=${file_location}`, getRequestOptions('delete'));
      }
    }
  }
};

export default GreenLeafApi;




// WEBPACK FOOTER //
// ./src/components/utils/GreenLeafApi.js